import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountTreeRounded,
  AddRounded,
  DeleteRounded,
  DragIndicatorRounded,
  EditRounded,
  FlagRounded,
  SettingsRounded,
} from '@mui/icons-material';
import IndustrialWorkflowsFieldsConfigSection from '../components/IndustrialWorkflowsFieldsConfigSection';
import IndustrialWorkflowsStageTaskTemplatesConfigSection from '../components/IndustrialWorkflowsStageTaskTemplatesConfigSection';
import IndustrialWorkflowsStageTransitionsConfigSection from '../components/IndustrialWorkflowsStageTransitionsConfigSection';
import IndustrialWorkflowsStageWorkReportsConfigSection from '../components/IndustrialWorkflowsStageWorkReportsConfigSection';
import IndustrialWorkflowsWorkAreasConfigSection from '../components/IndustrialWorkflowsWorkAreasConfigSection';
import {
  createIndustrialWorkflowProcess,
  createIndustrialWorkflowStage,
  deleteIndustrialWorkflowProcess,
  deleteIndustrialWorkflowStage,
  listIndustrialWorkflowProcesses,
  listIndustrialWorkflowStages,
  listIndustrialWorkflowWorkAreas,
  updateIndustrialWorkflowProcess,
  updateIndustrialWorkflowStage,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowProcessDefinition,
  IndustrialWorkflowProcessDefinitionUpsertPayload,
  IndustrialWorkflowStageDefinition,
  IndustrialWorkflowStageDefinitionUpsertPayload,
  IndustrialWorkflowStageType,
} from '../types/industrialWorkflows';

type ProcessFormState = {
  code: string;
  name: string;
  description: string;
  case_prefix: string;
  is_active: boolean;
  display_order: string;
};

type StageFormState = {
  code: string;
  name: string;
  description: string;
  color_key: string;
  display_order: string;

  is_initial: boolean;
  is_final: boolean;
  is_active: boolean;

  stage_type: IndustrialWorkflowStageType;

  execution_work_area_id: string;
  tracking_work_area_id: string;

  outside_system_actor: string;
  outside_system_action: string;

  expected_duration_days: string;
};

const EMPTY_PROCESS_FORM: ProcessFormState = {
  code: '',
  name: '',
  description: '',
  case_prefix: 'EXP',
  is_active: true,
  display_order: '0',
};

const EMPTY_STAGE_FORM: StageFormState = {
  code: '',
  name: '',
  description: '',
  color_key: 'slate',
  display_order: '0',

  is_initial: false,
  is_final: false,
  is_active: true,

  stage_type: 'app_work',

  execution_work_area_id: '',
  tracking_work_area_id: '',

  outside_system_actor: '',
  outside_system_action: '',

  expected_duration_days: '',
};

const STAGE_COLORS: Record<string, string> = {
  slate: '#94a3b8',
  blue: '#38bdf8',
  amber: '#fbbf24',
  green: '#4ade80',
  red: '#f87171',
  violet: '#a78bfa',
};

const OUTSIDE_SYSTEM_EXECUTION_VALUE =
  '__outside_system__';

function getStageTypeLabel(
  stageType: IndustrialWorkflowStageType,
) {
  if (stageType === 'entry') {
    return 'Entrada';
  }

  if (stageType === 'outside_system_wait') {
    return 'Espera externa';
  }

  if (stageType === 'closure') {
    return 'Cierre';
  }

  return 'Trabajo interno';
}

function getColorFromColorKey(
  colorKey: string | null | undefined,
) {
  return (
    STAGE_COLORS[colorKey ?? 'slate']
    ?? STAGE_COLORS.slate
  );
}

function getStageVisualColor(
  stage: IndustrialWorkflowStageDefinition,
  workAreaColorById: Map<string, string>,
) {
  if (stage.stage_type === 'outside_system_wait') {
    return STAGE_COLORS.amber;
  }

  if (stage.stage_type === 'closure') {
    return STAGE_COLORS.green;
  }

  if (stage.execution_work_area_id) {
    const executionAreaColor =
      workAreaColorById.get(
        stage.execution_work_area_id,
      );

    if (executionAreaColor) {
      return executionAreaColor;
    }
  }

  if (stage.tracking_work_area_id) {
    const trackingAreaColor =
      workAreaColorById.get(
        stage.tracking_work_area_id,
      );

    if (trackingAreaColor) {
      return trackingAreaColor;
    }
  }

  return getColorFromColorKey(stage.color_key);
}

function buildStageOwnerLabel(
  stage: IndustrialWorkflowStageDefinition,
  workAreaNameById: Map<string, string>,
) {
  if (stage.stage_type === 'outside_system_wait') {
    return (
      stage.outside_system_actor
      || 'Responsable externo'
    );
  }

  if (stage.execution_work_area_id) {
    return (
      workAreaNameById.get(
        stage.execution_work_area_id,
      )
      ?? 'Área no disponible'
    );
  }

  return null;
}

function getStageTypeAccentColor(
  stage: IndustrialWorkflowStageDefinition,
  stageColor: string,
) {
  if (stage.stage_type === 'outside_system_wait') {
    return STAGE_COLORS.amber;
  }

  if (stage.stage_type === 'closure') {
    return STAGE_COLORS.green;
  }

  if (stage.stage_type === 'entry') {
    return STAGE_COLORS.blue;
  }

  return stageColor;
}

function getStageTypeHint(
  stage: IndustrialWorkflowStageDefinition,
) {
  if (stage.stage_type === 'outside_system_wait') {
    return 'Pendiente de externo';
  }

  if (stage.stage_type === 'closure') {
    return 'Cierra expediente';
  }

  if (stage.stage_type === 'entry') {
    return 'Inicio del flujo';
  }

  return 'Trabajo interno';
}

function parseInteger(value: string, fallback = 0) {
  const parsedValue = Number.parseInt(value, 10);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function parseOptionalInteger(value: string) {
  if (!value.trim()) return null;

  const parsedValue = Number.parseInt(value, 10);

  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function normalizeConfigCode(value: string) {
  return value
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
}

function buildUniqueStageCode(
  name: string,
  stages: IndustrialWorkflowStageDefinition[],
) {
  const normalizedBaseCode =
    normalizeConfigCode(name)
    || 'ETAPA';

  const existingCodes =
    new Set(
      stages.map((stage) =>
        stage.code.trim().toUpperCase(),
      ),
    );

  if (!existingCodes.has(normalizedBaseCode)) {
    return normalizedBaseCode;
  }

  let suffix = 2;

  while (
    existingCodes.has(
      `${normalizedBaseCode}_${suffix}`,
    )
  ) {
    suffix += 1;
  }

  return `${normalizedBaseCode}_${suffix}`;
}

function getNextStageDisplayOrder(
  stages: IndustrialWorkflowStageDefinition[],
) {
  if (!stages.length) {
    return 10;
  }

  return (
    Math.max(
      ...stages.map(
        (stage) => stage.display_order,
      ),
    )
    + 10
  );
}

function buildUniqueProcessCode(
  name: string,
  processes: IndustrialWorkflowProcessDefinition[],
) {
  const normalizedBaseCode =
    normalizeConfigCode(name)
    || 'FLUJO';

  const existingCodes =
    new Set(
      processes.map((process) =>
        process.code.trim().toUpperCase(),
      ),
    );

  if (!existingCodes.has(normalizedBaseCode)) {
    return normalizedBaseCode;
  }

  let suffix = 2;

  while (
    existingCodes.has(
      `${normalizedBaseCode}_${suffix}`,
    )
  ) {
    suffix += 1;
  }

  return `${normalizedBaseCode}_${suffix}`;
}

function getNextProcessDisplayOrder(
  processes: IndustrialWorkflowProcessDefinition[],
) {
  if (!processes.length) {
    return 10;
  }

  return (
    Math.max(
      ...processes.map(
        (process) => process.display_order,
      ),
    )
    + 10
  );
}

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo completar la operación.';
}

function buildProcessPayload(
  form: ProcessFormState,
  processes: IndustrialWorkflowProcessDefinition[] = [],
): IndustrialWorkflowProcessDefinitionUpsertPayload {
  const resolvedCode =
    form.code.trim()
    || buildUniqueProcessCode(
      form.name,
      processes,
    );

  const resolvedDisplayOrder =
    form.display_order.trim()
      ? parseInteger(form.display_order)
      : getNextProcessDisplayOrder(processes);

  return {
    code: resolvedCode,
    name: form.name.trim(),
    description: form.description.trim() || null,
    case_prefix:
      form.case_prefix.trim()
      || 'EXP',
    is_active: form.is_active,
    display_order: resolvedDisplayOrder,
  };
}

function buildStagePayload(
  form: StageFormState,
  stages: IndustrialWorkflowStageDefinition[] = [],
): IndustrialWorkflowStageDefinitionUpsertPayload {
  const resolvedCode =
    form.code.trim()
    || buildUniqueStageCode(
      form.name,
      stages,
    );

  const resolvedDisplayOrder =
    form.display_order.trim()
      ? parseInteger(form.display_order)
      : getNextStageDisplayOrder(stages);

  return {
    code: resolvedCode,
    name: form.name,
    description: form.description.trim() || null,
    color_key: form.color_key.trim() || null,
    display_order: resolvedDisplayOrder,

    is_initial: form.is_initial,
    is_final: form.is_final,
    is_active: form.is_active,

    stage_type: form.stage_type,

    execution_work_area_id:
      form.execution_work_area_id || null,

    tracking_work_area_id:
      form.tracking_work_area_id || null,

    outside_system_actor:
      form.outside_system_actor.trim() || null,

    outside_system_action:
      form.outside_system_action.trim() || null,

    expected_duration_days: parseOptionalInteger(
      form.expected_duration_days,
    ),
  };
}

function buildStageFormState(
  stage: IndustrialWorkflowStageDefinition,
): StageFormState {
  return {
    code: stage.code,
    name: stage.name,
    description: stage.description ?? '',
    color_key: stage.color_key ?? 'slate',
    display_order: String(stage.display_order),

    is_initial: stage.is_initial,
    is_final: stage.is_final,
    is_active: stage.is_active,

    stage_type: stage.stage_type,

    execution_work_area_id:
      stage.execution_work_area_id ?? '',

    tracking_work_area_id:
      stage.tracking_work_area_id ?? '',

    outside_system_actor:
      stage.outside_system_actor ?? '',

    outside_system_action:
      stage.outside_system_action ?? '',

    expected_duration_days:
      stage.expected_duration_days === null
        ? ''
        : String(stage.expected_duration_days),
  };
}

export default function IndustrialWorkflowsConfigView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedProcessId, setSelectedProcessId] = useState<string | null>(
    null,
  );

  const [activeConfigSection, setActiveConfigSection] =
    useState<'flows' | 'areas'>('flows');

  const [selectedProcessTab, setSelectedProcessTab] =
    useState<'stages' | 'fields'>('stages');

  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [editingProcess, setEditingProcess] =
    useState<IndustrialWorkflowProcessDefinition | null>(null);
  const [processForm, setProcessForm] =
    useState<ProcessFormState>(EMPTY_PROCESS_FORM);

  const [processPendingDelete, setProcessPendingDelete] =
    useState<IndustrialWorkflowProcessDefinition | null>(null);

  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [editingStage, setEditingStage] =
    useState<IndustrialWorkflowStageDefinition | null>(null);
  const [stageForm, setStageForm] =
    useState<StageFormState>(EMPTY_STAGE_FORM);
  const [configuringStage, setConfiguringStage] =
    useState<IndustrialWorkflowStageDefinition | null>(null);

  const [stagePendingDelete, setStagePendingDelete] =
    useState<IndustrialWorkflowStageDefinition | null>(null);

  const [draggingStageId, setDraggingStageId] =
    useState<string | null>(null);

  const processesQuery = useQuery({
    queryKey: ['industrial-workflows-processes', true],
    queryFn: () => listIndustrialWorkflowProcesses(true),
  });

  const workAreasQuery = useQuery({
    queryKey: [
      'industrial-workflows-work-areas',
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowWorkAreas(true),
  });

  const workAreaNameById = useMemo(
    () =>
      new Map(
        (workAreasQuery.data ?? []).map(
          (area) => [
            area.id,
            area.name,
          ],
        ),
      ),
    [workAreasQuery.data],
  );

  const workAreaColorById = useMemo(
    () =>
      new Map(
        (workAreasQuery.data ?? []).map(
          (area) => [
            area.id,
            getColorFromColorKey(
              area.color_key,
            ),
          ],
        ),
      ),
    [workAreasQuery.data],
  );

  const stagesQuery = useQuery({
    queryKey: [
      'industrial-workflows-stages',
      selectedProcessId,
      true,
    ],
    queryFn: () =>
      listIndustrialWorkflowStages(
        selectedProcessId as string,
        true,
      ),
    enabled: Boolean(selectedProcessId),
  });

  useEffect(() => {
    const processes = processesQuery.data ?? [];

    if (!processes.length) {
      setSelectedProcessId(null);
      return;
    }

    const selectedProcessStillExists = processes.some(
      (process) => process.id === selectedProcessId,
    );

    if (!selectedProcessStillExists) {
      setSelectedProcessId(processes[0].id);
    }
  }, [processesQuery.data, selectedProcessId]);

  const selectedProcess = useMemo(
    () =>
      (processesQuery.data ?? []).find(
        (process) => process.id === selectedProcessId,
      ) ?? null,
    [processesQuery.data, selectedProcessId],
  );

  const orderedStages = useMemo(
    () =>
      [...(stagesQuery.data ?? [])].sort(
        (left, right) =>
          left.display_order
          - right.display_order
          || left.name.localeCompare(
            right.name,
          ),
      ),
    [stagesQuery.data],
  );

  const createProcessMutation = useMutation({
    mutationFn: (
      payload: IndustrialWorkflowProcessDefinitionUpsertPayload,
    ) => createIndustrialWorkflowProcess(payload),
    onSuccess: async (createdProcess) => {
      await queryClient.invalidateQueries({
        queryKey: ['industrial-workflows-processes'],
      });

      setSelectedProcessId(createdProcess.id);
      setProcessDialogOpen(false);
      enqueueSnackbar('Flujo creado correctamente.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const updateProcessMutation = useMutation({
    mutationFn: ({
      processDefinitionId,
      payload,
    }: {
      processDefinitionId: string;
      payload: IndustrialWorkflowProcessDefinitionUpsertPayload;
    }) =>
      updateIndustrialWorkflowProcess(
        processDefinitionId,
        payload,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['industrial-workflows-processes'],
      });

      setProcessDialogOpen(false);
      enqueueSnackbar('Flujo actualizado correctamente.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const deleteProcessMutation = useMutation({
    mutationFn: (
      processDefinitionId: string,
    ) =>
      deleteIndustrialWorkflowProcess(
        processDefinitionId,
      ),
    onSuccess: async (_, deletedProcessDefinitionId) => {
      await queryClient.invalidateQueries({
        queryKey: ['industrial-workflows-processes'],
      });

      if (
        selectedProcessId
        === deletedProcessDefinitionId
      ) {
        setSelectedProcessId(null);
      }

      setProcessPendingDelete(null);
      setProcessDialogOpen(false);
      setEditingProcess(null);
      setProcessForm(EMPTY_PROCESS_FORM);

      enqueueSnackbar('Flujo eliminado correctamente.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const createStageMutation = useMutation({
    mutationFn: ({
      processDefinitionId,
      payload,
    }: {
      processDefinitionId: string;
      payload: IndustrialWorkflowStageDefinitionUpsertPayload;
    }) =>
      createIndustrialWorkflowStage(
        processDefinitionId,
        payload,
      ),
    onSuccess: async (createdStage) => {
      await queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-stages',
          selectedProcessId,
        ],
      });

      setStageDialogOpen(false);
      setConfiguringStage(createdStage);
      setEditingStage(createdStage);
      setStageForm(
        buildStageFormState(createdStage),
      );

      enqueueSnackbar('Etapa creada correctamente.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const updateStageMutation = useMutation({
    mutationFn: ({
      processDefinitionId,
      stageDefinitionId,
      payload,
    }: {
      processDefinitionId: string;
      stageDefinitionId: string;
      payload: IndustrialWorkflowStageDefinitionUpsertPayload;
    }) =>
      updateIndustrialWorkflowStage(
        processDefinitionId,
        stageDefinitionId,
        payload,
      ),
    onSuccess: async (updatedStage) => {
      await queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-stages',
          selectedProcessId,
        ],
      });

      setConfiguringStage(updatedStage);
      setEditingStage(updatedStage);
      setStageForm(buildStageFormState(updatedStage));

      enqueueSnackbar('Etapa actualizada correctamente.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const deleteStageMutation = useMutation({
    mutationFn: ({
      processDefinitionId,
      stageDefinitionId,
    }: {
      processDefinitionId: string;
      stageDefinitionId: string;
    }) =>
      deleteIndustrialWorkflowStage(
        processDefinitionId,
        stageDefinitionId,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-stages',
          selectedProcessId,
        ],
      });

      setStagePendingDelete(null);
      setConfiguringStage(null);
      setEditingStage(null);
      setStageDialogOpen(false);
      setStageForm(EMPTY_STAGE_FORM);

      enqueueSnackbar('Etapa eliminada correctamente.', {
        variant: 'success',
      });
    },
    onError: (error) => {
      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
  });

  const reorderStagesMutation = useMutation({
    mutationFn: async (
      reorderedStages: IndustrialWorkflowStageDefinition[],
    ) => {
      if (!selectedProcessId) {
        return;
      }

      await Promise.all(
        reorderedStages.map(
          (stage, stageIndex) =>
            updateIndustrialWorkflowStage(
              selectedProcessId,
              stage.id,
              {
                code: stage.code,
                name: stage.name,
                description: stage.description,
                color_key: stage.color_key,
                display_order:
                  (stageIndex + 1) * 10,

                is_initial: stage.is_initial,
                is_final: stage.is_final,
                is_active: stage.is_active,

                stage_type: stage.stage_type,

                execution_work_area_id:
                  stage.execution_work_area_id,

                tracking_work_area_id:
                  stage.tracking_work_area_id,

                outside_system_actor:
                  stage.outside_system_actor,

                outside_system_action:
                  stage.outside_system_action,

                expected_duration_days:
                  stage.expected_duration_days,
              },
            ),
        ),
      );
    },
    onMutate: async (
      reorderedStages,
    ) => {
      const queryKey = [
        'industrial-workflows-stages',
        selectedProcessId,
        true,
      ];

      await queryClient.cancelQueries({
        queryKey,
      });

      const previousStages =
        queryClient.getQueryData<
          IndustrialWorkflowStageDefinition[]
        >(queryKey);

      queryClient.setQueryData<
        IndustrialWorkflowStageDefinition[]
      >(
        queryKey,
        reorderedStages.map(
          (stage, stageIndex) => ({
            ...stage,
            display_order:
              (stageIndex + 1) * 10,
          }),
        ),
      );

      return {
        queryKey,
        previousStages,
      };
    },
    onSuccess: async () => {
      enqueueSnackbar(
        'Orden de etapas actualizado.',
        {
          variant: 'success',
        },
      );
    },
    onError: (
      error,
      _reorderedStages,
      context,
    ) => {
      if (
        context?.queryKey
        && context.previousStages
      ) {
        queryClient.setQueryData(
          context.queryKey,
          context.previousStages,
        );
      }

      enqueueSnackbar(getErrorMessage(error), {
        variant: 'error',
      });
    },
    onSettled: async () => {
      setDraggingStageId(null);

      await queryClient.invalidateQueries({
        queryKey: [
          'industrial-workflows-stages',
          selectedProcessId,
        ],
      });
    },
  });

  const handleCreateProcess = () => {
    const existingProcesses =
      processesQuery.data ?? [];

    setEditingProcess(null);
    setProcessForm({
      ...EMPTY_PROCESS_FORM,
      display_order: String(
        getNextProcessDisplayOrder(
          existingProcesses,
        ),
      ),
      is_active: true,
    });
    setProcessDialogOpen(true);
  };

  const handleEditProcess = (
    process: IndustrialWorkflowProcessDefinition,
  ) => {
    setEditingProcess(process);
    setProcessForm({
      code: process.code,
      name: process.name,
      description: process.description ?? '',
      case_prefix: process.case_prefix,
      is_active: process.is_active,
      display_order: String(process.display_order),
    });
    setProcessDialogOpen(true);
  };

  const handleSaveProcess = () => {
    if (!processForm.name.trim()) {
      enqueueSnackbar(
        'Indica el nombre del flujo.',
        { variant: 'warning' },
      );
      return;
    }

    const payload = buildProcessPayload(
      processForm,
      processesQuery.data ?? [],
    );

    if (editingProcess) {
      updateProcessMutation.mutate({
        processDefinitionId: editingProcess.id,
        payload,
      });

      return;
    }

    createProcessMutation.mutate(payload);
  };

  const handleCreateStage = () => {
    const existingStages =
      stagesQuery.data ?? [];

    const isFirstStage =
      existingStages.length === 0;

    setEditingStage(null);
    setStageForm({
      ...EMPTY_STAGE_FORM,
      display_order: String(
        getNextStageDisplayOrder(
          existingStages,
        ),
      ),
      is_initial: isFirstStage,
      stage_type: isFirstStage
        ? 'entry'
        : 'app_work',
    });
    setStageDialogOpen(true);
  };

  const handleConfigureStage = (
    stage: IndustrialWorkflowStageDefinition,
  ) => {
    setEditingStage(stage);
    setStageForm(buildStageFormState(stage));
    setConfiguringStage(stage);
  };

  const handleSaveStage = () => {
    if (!selectedProcessId) {
      enqueueSnackbar(
        'Selecciona primero un proceso.',
        { variant: 'error' },
      );
      return;
    }

    if (!stageForm.name.trim()) {
      enqueueSnackbar(
        'Indica el nombre de la etapa.',
        { variant: 'warning' },
      );
      return;
    }

    const payload = buildStagePayload(
      stageForm,
      stagesQuery.data ?? [],
    );

    if (editingStage) {
      updateStageMutation.mutate({
        processDefinitionId: selectedProcessId,
        stageDefinitionId: editingStage.id,
        payload,
      });

      return;
    }

    createStageMutation.mutate({
      processDefinitionId: selectedProcessId,
      payload,
    });
  };

  const processMutationIsPending =
    createProcessMutation.isPending ||
    updateProcessMutation.isPending ||
    deleteProcessMutation.isPending;

  const stageMutationIsPending =
    createStageMutation.isPending ||
    updateStageMutation.isPending ||
    deleteStageMutation.isPending ||
    reorderStagesMutation.isPending;

  const handleConfirmDeleteProcess = () => {
    if (!processPendingDelete) {
      return;
    }

    deleteProcessMutation.mutate(
      processPendingDelete.id,
    );
  };

  const handleConfirmDeleteStage = () => {
    if (
      !selectedProcessId
      || !stagePendingDelete
    ) {
      return;
    }

    deleteStageMutation.mutate({
      processDefinitionId: selectedProcessId,
      stageDefinitionId: stagePendingDelete.id,
    });
  };

  const handleCloseStageConfiguration = () => {
    if (stageMutationIsPending) {
      return;
    }

    setConfiguringStage(null);
    setEditingStage(null);
    setStageForm(EMPTY_STAGE_FORM);
  };

  const handleStageDrop = (
    targetStageId: string,
  ) => {
    if (
      !draggingStageId
      || draggingStageId === targetStageId
      || reorderStagesMutation.isPending
    ) {
      setDraggingStageId(null);
      return;
    }

    const fromIndex =
      orderedStages.findIndex(
        (stage) =>
          stage.id === draggingStageId,
      );

    const toIndex =
      orderedStages.findIndex(
        (stage) =>
          stage.id === targetStageId,
      );

    if (
      fromIndex < 0
      || toIndex < 0
    ) {
      setDraggingStageId(null);
      return;
    }

    const nextStages =
      [...orderedStages];

    const [movedStage] =
      nextStages.splice(
        fromIndex,
        1,
      );

    nextStages.splice(
      toIndex,
      0,
      movedStage,
    );

    reorderStagesMutation.mutate(
      nextStages.map(
        (stage, stageIndex) => ({
          ...stage,
          display_order:
            (stageIndex + 1) * 10,
        }),
      ),
    );
  };

  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#38bdf8', 0.18)}`,
          bgcolor: 'rgba(15,23,42,0.78)',
          px: {
            xs: 2,
            md: 2.4,
          },
          py: {
            xs: 1.75,
            md: 2,
          },
        }}
      >
        <Stack spacing={0.5}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <SettingsRounded sx={{ color: '#38bdf8' }} />

            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
                fontSize: '1.35rem',
              }}
            >
              Configuración del organizador
            </Typography>
          </Stack>

          <Typography
            sx={{
              color: '#94a3b8',
              maxWidth: 820,
              fontSize: '0.92rem',
            }}
          >
            Define los flujos, las etapas y los responsables
            de trabajo de tu organización.
          </Typography>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: `1px solid ${alpha('#94a3b8', 0.14)}`,
          bgcolor: 'rgba(15,23,42,0.66)',
          px: 0.8,
        }}
      >
        <Tabs
          value={activeConfigSection}
          onChange={(
            _event,
            nextValue: 'flows' | 'areas',
          ) => setActiveConfigSection(nextValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontWeight: 900,
            },
          }}
        >
          <Tab
            value="flows"
            label="Flujos y etapas"
          />

          <Tab
            value="areas"
            label="Áreas de trabajo"
          />
        </Tabs>
      </Paper>

      {activeConfigSection === 'areas' ? (
        <IndustrialWorkflowsWorkAreasConfigSection />
      ) : (

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '360px minmax(0, 1fr)',
            xl: '380px minmax(0, 1fr)',
          },
          gap: 2.75,
          alignItems: 'start',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${alpha('#94a3b8', 0.14)}`,
            bgcolor: 'rgba(15,23,42,0.66)',
            p: 1.35,
            position: 'sticky',
            top: 12,
          }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={0.8}
              sx={{
                px: 0.75,
                pt: 0.4,
              }}
            >
              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontWeight: 900,
                  fontSize: '0.78rem',
                  letterSpacing: 0.7,
                  textTransform: 'uppercase',
                }}
              >
                Flujos de trabajo
              </Typography>

              <Button
                variant="outlined"
                size="small"
                startIcon={<AddRounded />}
                onClick={handleCreateProcess}
                sx={{
                  minHeight: 32,
                  px: 1.25,
                  fontSize: '0.78rem',
                  fontWeight: 850,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  borderRadius: 2,
                  borderColor: alpha('#38bdf8', 0.34),
                  color: '#67e8f9',
                  '& .MuiButton-startIcon': {
                    mr: 0.55,
                  },
                  '&:hover': {
                    borderColor: alpha('#38bdf8', 0.58),
                    bgcolor: alpha('#38bdf8', 0.08),
                  },
                }}
              >
                Nuevo flujo
              </Button>
            </Stack>

            {processesQuery.isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  py: 4,
                }}
              >
                <CircularProgress size={26} />
              </Box>
            ) : null}

            {!processesQuery.isLoading &&
            !(processesQuery.data ?? []).length ? (
              <Alert
                severity="info"
                variant="outlined"
                sx={{
                  bgcolor: 'rgba(14,116,144,0.08)',
                }}
              >
                Todavía no hay flujos configurados.
              </Alert>
            ) : null}

            <Stack spacing={0.85}>
              {(processesQuery.data ?? []).map((process) => {
                const selected =
                  process.id === selectedProcessId;

                return (
                  <Paper
                    key={process.id}
                    elevation={0}
                    onClick={() =>
                      setSelectedProcessId(process.id)
                    }
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 3,
                      border: `1px solid ${
                        selected
                          ? alpha('#38bdf8', 0.5)
                          : alpha('#94a3b8', 0.12)
                      }`,
                      bgcolor: selected
                        ? alpha('#0ea5e9', 0.13)
                        : 'rgba(15,23,42,0.36)',
                      px: 1.15,
                      py: 1,
                      transition:
                        'border-color 0.18s ease, background-color 0.18s ease, transform 0.18s ease',
                      boxShadow: selected
                        ? `inset 4px 0 0 ${alpha(
                            '#38bdf8',
                            0.72,
                          )}`
                        : 'none',
                      '&:hover': {
                        borderColor: alpha('#38bdf8', 0.38),
                        bgcolor: alpha('#0ea5e9', 0.08),
                        transform: 'translateX(2px)',
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.05}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          minWidth: 34,
                          borderRadius: 2.2,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: selected
                            ? alpha('#38bdf8', 0.16)
                            : alpha('#64748b', 0.12),
                          border: `1px solid ${
                            selected
                              ? alpha('#38bdf8', 0.28)
                              : alpha('#94a3b8', 0.12)
                          }`,
                        }}
                      >
                        <AccountTreeRounded
                          fontSize="small"
                          sx={{
                            color: selected
                              ? '#67e8f9'
                              : '#94a3b8',
                          }}
                        />
                      </Box>

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 950,
                            lineHeight: 1.18,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {process.name}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={0.6}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                          sx={{
                            mt: 0.65,
                          }}
                        >
                          <Chip
                            size="small"
                            label={
                              process.is_active
                                ? 'Disponible'
                                : 'Inactivo'
                            }
                            sx={{
                              height: 22,
                              color: process.is_active
                                ? '#bbf7d0'
                                : '#cbd5e1',
                              bgcolor: process.is_active
                                ? alpha('#22c55e', 0.13)
                                : alpha('#64748b', 0.14),
                              fontWeight: 800,
                            }}
                          />
                        </Stack>
                      </Box>

                      <IconButton
                        size="small"
                        aria-label="Editar flujo"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEditProcess(process);
                        }}
                        sx={{
                          color: selected
                            ? '#bae6fd'
                            : '#94a3b8',
                          bgcolor: selected
                            ? alpha('#38bdf8', 0.08)
                            : 'transparent',
                          '&:hover': {
                            bgcolor: alpha('#38bdf8', 0.14),
                          },
                        }}
                      >
                        <EditRounded fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${alpha('#94a3b8', 0.14)}`,
            bgcolor: 'rgba(15,23,42,0.66)',
            p: { xs: 1.75, md: 2.25 },
          }}
        >
          {!selectedProcess ? (
            <Alert severity="info" variant="outlined">
              Crea o selecciona un flujo de trabajo para configurar
              sus etapas.
            </Alert>
          ) : (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{
                  xs: 'flex-start',
                  md: 'center',
                }}
                spacing={1.5}
              >
                <Stack spacing={0.4}>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontWeight: 950,
                      fontSize: '1.18rem',
                    }}
                  >
                    {selectedProcess.name}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: '0.86rem',
                    }}
                  >
                    Etapas ordenadas del flujo
                  </Typography>
                </Stack>

                {selectedProcessTab === 'stages' ? (
                  <Button
                    variant="outlined"
                    startIcon={<AddRounded />}
                    onClick={handleCreateStage}
                    sx={{
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    Nueva etapa
                  </Button>
                ) : null}
              </Stack>

              <Divider
                sx={{
                  borderColor: alpha('#94a3b8', 0.14),
                }}
              />

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(
                    '#94a3b8',
                    0.12,
                  )}`,
                  bgcolor: 'rgba(2,6,23,0.18)',
                  px: 0.5,
                }}
              >
                <Tabs
                  value={selectedProcessTab}
                  onChange={(
                    _event,
                    nextValue: 'stages' | 'fields',
                  ) => setSelectedProcessTab(nextValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                  sx={{
                    minHeight: 44,
                    '& .MuiTab-root': {
                      minHeight: 44,
                      textTransform: 'none',
                      fontWeight: 900,
                    },
                  }}
                >
                  <Tab
                    value="stages"
                    label="Etapas"
                  />

                  <Tab
                    value="fields"
                    label="Campos del expediente"
                  />
                </Tabs>
              </Paper>

              {selectedProcessTab === 'stages' ? (
                <>
                  {stagesQuery.isLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 4,
                  }}
                >
                  <CircularProgress size={26} />
                </Box>
              ) : null}

              {!stagesQuery.isLoading &&
              !orderedStages.length ? (
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{
                    bgcolor: 'rgba(14,116,144,0.08)',
                  }}
                >
                  Todavía no hay etapas configuradas.
                </Alert>
              ) : null}

              <Stack spacing={1}>
                {orderedStages.map((stage) => {
                  const stageColor =
                    getStageVisualColor(
                      stage,
                      workAreaColorById,
                    );

                  const ownerLabel =
                    buildStageOwnerLabel(
                      stage,
                      workAreaNameById,
                    );

                  const stageTypeAccentColor =
                    getStageTypeAccentColor(
                      stage,
                      stageColor,
                    );

                  const isExternalWaitStage =
                    stage.stage_type
                    === 'outside_system_wait';

                  const isClosureStage =
                    stage.stage_type === 'closure';

                  const trackingLabel =
                    stage.tracking_work_area_id
                      ? workAreaNameById.get(
                          stage.tracking_work_area_id,
                        )
                        ?? 'Área no disponible'
                      : null;

                  const isDragging =
                    draggingStageId === stage.id;

                  return (
                    <Paper
                      key={stage.id}
                      elevation={0}
                      onDragOver={(event) => {
                        event.preventDefault();
                      }}
                      onDrop={() =>
                        handleStageDrop(stage.id)
                      }
                      sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha(
                          stageTypeAccentColor,
                          isDragging ? 0.64 : 0.34,
                        )}`,
                        bgcolor: isExternalWaitStage
                          ? alpha(
                              STAGE_COLORS.amber,
                              isDragging ? 0.16 : 0.095,
                            )
                          : alpha(
                              stageColor,
                              isDragging ? 0.11 : 0.055,
                            ),
                        boxShadow: isExternalWaitStage
                          ? `inset 4px 0 0 ${alpha(
                              STAGE_COLORS.amber,
                              0.72,
                            )}`
                          : isClosureStage
                          ? `inset 4px 0 0 ${alpha(
                              STAGE_COLORS.green,
                              0.68,
                            )}`
                          : `inset 4px 0 0 ${alpha(
                              stageColor,
                              0.55,
                            )}`,
                        px: 1.35,
                        py: 1.2,
                        opacity: isDragging ? 0.72 : 1,
                        transition:
                          'border-color 0.16s ease, background-color 0.16s ease, opacity 0.16s ease',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.15}
                        alignItems="center"
                      >
                        <Box
                          draggable={
                            true
                          }
                          onDragStart={() =>
                            setDraggingStageId(stage.id)
                          }
                          onDragEnd={() =>
                            setDraggingStageId(null)
                          }
                          sx={{
                            width: 30,
                            minWidth: 30,
                            height: 38,
                            borderRadius: 2,
                            display: 'grid',
                            placeItems: 'center',
                            cursor:
                              reorderStagesMutation.isPending
                                ? 'not-allowed'
                                : 'grab',
                            color: alpha('#cbd5e1', 0.8),
                            bgcolor: alpha(
                              '#020617',
                              0.18,
                            ),
                            '&:active': {
                              cursor: 'grabbing',
                            },
                          }}
                          title="Arrastra para reordenar"
                        >
                          <DragIndicatorRounded
                            fontSize="small"
                          />
                        </Box>

                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            minWidth: 34,
                            borderRadius: 2.2,
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: alpha(
                              stageTypeAccentColor,
                              isExternalWaitStage ? 0.2 : 0.14,
                            ),
                            border: `1px solid ${alpha(
                              stageTypeAccentColor,
                              isExternalWaitStage ? 0.42 : 0.26,
                            )}`,
                          }}
                        >
                          <FlagRounded
                            fontSize="small"
                            sx={{
                              color: stageTypeAccentColor,
                            }}
                          />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={0.8}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <Typography
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 950,
                              }}
                            >
                              {stage.name}
                            </Typography>

                            <Chip
                              size="small"
                              label={getStageTypeHint(stage)}
                              sx={{
                                color: isExternalWaitStage
                                  ? '#fde68a'
                                  : isClosureStage
                                  ? '#dcfce7'
                                  : '#dbeafe',
                                bgcolor: alpha(
                                  stageTypeAccentColor,
                                  isExternalWaitStage ? 0.24 : 0.16,
                                ),
                                border: `1px solid ${alpha(
                                  stageTypeAccentColor,
                                  isExternalWaitStage ? 0.34 : 0.18,
                                )}`,
                                fontWeight: 850,
                              }}
                            />

                            <Chip
                              size="small"
                              label={getStageTypeLabel(
                                stage.stage_type,
                              )}
                              sx={{
                                color: '#cbd5e1',
                                bgcolor: alpha(
                                  '#020617',
                                  0.24,
                                ),
                                border: `1px solid ${alpha(
                                  '#94a3b8',
                                  0.14,
                                )}`,
                                fontWeight: 750,
                              }}
                            />

                            {stage.is_initial ? (
                              <Chip
                                size="small"
                                label="Inicial"
                                sx={{
                                  color: '#bbf7d0',
                                  bgcolor: alpha('#22c55e', 0.15),
                                  fontWeight: 800,
                                }}
                              />
                            ) : null}

                            {stage.is_final ? (
                              <Chip
                                size="small"
                                label="Final"
                                sx={{
                                  color: '#bfdbfe',
                                  bgcolor: alpha('#3b82f6', 0.16),
                                  fontWeight: 800,
                                }}
                              />
                            ) : null}

                            {!stage.is_active ? (
                              <Chip
                                size="small"
                                label="Inactiva"
                                sx={{
                                  color: '#cbd5e1',
                                  bgcolor: alpha('#64748b', 0.14),
                                  fontWeight: 800,
                                }}
                              />
                            ) : null}
                          </Stack>

                          <Stack
                            direction="row"
                            spacing={0.7}
                            alignItems="center"
                            flexWrap="wrap"
                            useFlexGap
                            sx={{
                              mt: 0.7,
                            }}
                          >
                            {ownerLabel ? (
                              <Chip
                                size="small"
                                label={
                                  stage.stage_type
                                  === 'outside_system_wait'
                                    ? `Espera: ${ownerLabel}`
                                    : `Ejecuta: ${ownerLabel}`
                                }
                                sx={{
                                  color: '#cbd5e1',
                                  bgcolor: alpha(
                                    '#020617',
                                    0.24,
                                  ),
                                  border: `1px solid ${alpha(
                                    stageColor,
                                    0.18,
                                  )}`,
                                }}
                              />
                            ) : null}

                            {trackingLabel ? (
                              <Chip
                                size="small"
                                label={`Seguimiento: ${trackingLabel}`}
                                sx={{
                                  color: '#cbd5e1',
                                  bgcolor: alpha(
                                    '#020617',
                                    0.24,
                                  ),
                                  border: `1px solid ${alpha(
                                    '#94a3b8',
                                    0.16,
                                  )}`,
                                }}
                              />
                            ) : null}

                            {stage.expected_duration_days !== null ? (
                              <Chip
                                size="small"
                                label={`${stage.expected_duration_days} días`}
                                sx={{
                                  color: '#cbd5e1',
                                  bgcolor: alpha(
                                    '#020617',
                                    0.24,
                                  ),
                                  border: `1px solid ${alpha(
                                    '#94a3b8',
                                    0.16,
                                  )}`,
                                }}
                              />
                            ) : null}
                          </Stack>
                        </Box>

                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<SettingsRounded />}
                          onClick={() =>
                            handleConfigureStage(stage)
                          }
                          sx={{
                            fontWeight: 850,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Configurar
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
                </>
              ) : (
                <IndustrialWorkflowsFieldsConfigSection
                  processDefinitionId={selectedProcess.id}
                />
              )}
            </Stack>
          )}
        </Paper>
      </Box>
      )}

      <Dialog
        open={Boolean(configuringStage)}
        onClose={handleCloseStageConfiguration}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          {configuringStage
            ? `Configurar etapa: ${configuringStage.name}`
            : 'Configurar etapa'}
        </DialogTitle>

        <DialogContent dividers>
          {configuringStage && selectedProcess ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: `1px solid ${alpha(
                    '#94a3b8',
                    0.2,
                  )}`,
                  bgcolor: 'rgba(15,23,42,0.66)',
                  px: {
                    xs: 1.6,
                    md: 2,
                  },
                  py: 1.7,
                }}
              >
                <Stack spacing={1.5}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <SettingsRounded
                      sx={{
                        color: '#7dd3fc',
                      }}
                    />

                    <Box>
                      <Typography
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 950,
                        }}
                      >
                        Información general
                      </Typography>

                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: '0.82rem',
                          mt: 0.2,
                        }}
                      >
                        Define los datos esenciales y quién debe
                        gestionar esta etapa.
                      </Typography>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(0, 1fr))',
                      },
                      gap: 1.2,
                    }}
                  >
                    <TextField
                      label="Nombre"
                      value={stageForm.name}
                      onChange={(event) =>
                        setStageForm((current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                      fullWidth
                      required
                    />

                    <TextField
                      label="Plazo general de la etapa en días"
                      value={stageForm.expected_duration_days}
                      onChange={(event) =>
                        setStageForm((current) => ({
                          ...current,
                          expected_duration_days:
                            event.target.value,
                        }))
                      }
                      type="number"
                      fullWidth
                    />

                    <TextField
                      select
                      label="Área ejecutora"
                      value={
                        stageForm.stage_type
                        === 'outside_system_wait'
                          ? OUTSIDE_SYSTEM_EXECUTION_VALUE
                          : stageForm.execution_work_area_id
                      }
                      onChange={(event) => {
                        const nextExecutionValue =
                          event.target.value;

                        setStageForm((current) => {
                          if (
                            nextExecutionValue
                            === OUTSIDE_SYSTEM_EXECUTION_VALUE
                          ) {
                            return {
                              ...current,
                              stage_type:
                                'outside_system_wait',
                              execution_work_area_id: '',
                            };
                          }

                          return {
                            ...current,
                            stage_type:
                              current.is_initial
                                ? 'entry'
                                : 'app_work',
                            execution_work_area_id:
                              nextExecutionValue,
                            outside_system_actor: '',
                            outside_system_action: '',
                          };
                        });
                      }}
                      helperText="Selecciona el área que realiza el trabajo o indica que la acción sucede fuera de la app."
                      disabled={stageForm.is_final}
                      fullWidth
                    >
                      <MenuItem value="">
                        Sin área ejecutora todavía
                      </MenuItem>

                      {(workAreasQuery.data ?? [])
                        .filter(
                          (area) =>
                            area.is_active,
                        )
                        .map(
                          (area) => (
                            <MenuItem
                              key={area.id}
                              value={area.id}
                            >
                              {area.name}
                            </MenuItem>
                          ),
                        )}

                      <MenuItem
                        value={
                          OUTSIDE_SYSTEM_EXECUTION_VALUE
                        }
                      >
                        Acción o respuesta fuera del sistema
                      </MenuItem>
                    </TextField>

                    <TextField
                      select
                      label="Área de seguimiento"
                      value={
                        stageForm.tracking_work_area_id
                      }
                      onChange={(event) =>
                        setStageForm((current) => ({
                          ...current,
                          tracking_work_area_id:
                            event.target.value,
                        }))
                      }
                      helperText="Área responsable de comprobar que el expediente continúe avanzando."
                      disabled={stageForm.is_final}
                      fullWidth
                    >
                      <MenuItem value="">
                        Sin área de seguimiento todavía
                      </MenuItem>

                      {(workAreasQuery.data ?? [])
                        .filter(
                          (area) =>
                            area.is_active,
                        )
                        .map(
                          (area) => (
                            <MenuItem
                              key={area.id}
                              value={area.id}
                            >
                              {area.name}
                            </MenuItem>
                          ),
                        )}
                    </TextField>
                  </Box>

                  <TextField
                    label="Descripción"
                    value={stageForm.description}
                    onChange={(event) =>
                      setStageForm((current) => ({
                        ...current,
                        description: event.target.value,
                      }))
                    }
                    multiline
                    minRows={2}
                    fullWidth
                  />

                  {stageForm.stage_type
                  === 'outside_system_wait'
                  && !stageForm.is_final ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          md: 'repeat(2, minmax(0, 1fr))',
                        },
                        gap: 1.2,
                      }}
                    >
                      <TextField
                        label="Actor o responsable fuera del sistema"
                        value={
                          stageForm.outside_system_actor
                        }
                        onChange={(event) =>
                          setStageForm((current) => ({
                            ...current,
                            outside_system_actor:
                              event.target.value,
                          }))
                        }
                        helperText="Ejemplo: cliente, fábrica interna, proveedor o laboratorio."
                        fullWidth
                      />

                      <TextField
                        label="Acción o respuesta esperada"
                        value={
                          stageForm.outside_system_action
                        }
                        onChange={(event) =>
                          setStageForm((current) => ({
                            ...current,
                            outside_system_action:
                              event.target.value,
                          }))
                        }
                        helperText="Describe qué debe suceder antes de continuar."
                        multiline
                        minRows={2}
                        fullWidth
                      />
                    </Box>
                  ) : null}

                  <Stack
                    direction={{
                      xs: 'column',
                      md: 'row',
                    }}
                    spacing={{
                      xs: 0.25,
                      md: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={stageForm.is_initial}
                          disabled={stageForm.is_final}
                          onChange={(event) =>
                            setStageForm((current) => ({
                              ...current,
                              is_initial:
                                event.target.checked,
                              stage_type:
                                event.target.checked
                                  ? current.stage_type
                                    === 'outside_system_wait'
                                    ? 'outside_system_wait'
                                    : 'entry'
                                  : current.stage_type
                                    === 'entry'
                                    ? 'app_work'
                                    : current.stage_type,
                            }))
                          }
                        />
                      }
                      label="Etapa inicial del proceso"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={stageForm.is_final}
                          onChange={(event) =>
                            setStageForm((current) => {
                              const nextIsFinal =
                                event.target.checked;

                              return {
                                ...current,
                                is_final: nextIsFinal,
                                is_initial:
                                  nextIsFinal
                                    ? false
                                    : current.is_initial,
                                stage_type:
                                  nextIsFinal
                                    ? 'closure'
                                    : current.is_initial
                                      ? 'entry'
                                      : 'app_work',
                                execution_work_area_id:
                                  nextIsFinal
                                    ? ''
                                    : current
                                        .execution_work_area_id,
                                tracking_work_area_id:
                                  nextIsFinal
                                    ? ''
                                    : current
                                        .tracking_work_area_id,
                                outside_system_actor:
                                  nextIsFinal
                                    ? ''
                                    : current
                                        .outside_system_actor,
                                outside_system_action:
                                  nextIsFinal
                                    ? ''
                                    : current
                                        .outside_system_action,
                              };
                            })
                          }
                        />
                      }
                      label="Esta etapa cierra el expediente"
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={stageForm.is_active}
                          onChange={(event) =>
                            setStageForm((current) => ({
                              ...current,
                              is_active:
                                event.target.checked,
                            }))
                          }
                        />
                      }
                      label="Etapa activa"
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                  >
                    <Button
                      variant="contained"
                      disabled={
                        stageMutationIsPending
                        || !stageForm.name.trim()
                      }
                      onClick={handleSaveStage}
                      sx={{
                        fontWeight: 900,
                        textTransform: 'none',
                      }}
                    >
                      Guardar datos generales
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              <IndustrialWorkflowsStageWorkReportsConfigSection
                key={`work-reports-${configuringStage.id}`}
                processDefinitionId={selectedProcess.id}
                stageDefinitionId={configuringStage.id}
              />

              <IndustrialWorkflowsStageTaskTemplatesConfigSection
                key={`tasks-${configuringStage.id}`}
                processDefinitionId={selectedProcess.id}
                stageDefinitionId={configuringStage.id}
              />

              <IndustrialWorkflowsStageTransitionsConfigSection
                key={`transitions-${configuringStage.id}`}
                processDefinitionId={selectedProcess.id}
                stages={stagesQuery.data ?? []}
                initialStageId={configuringStage.id}
                hideStageSelector
              />
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: 1 }}
          >
            <Button
              color="error"
              variant="outlined"
              startIcon={<DeleteRounded />}
              disabled={
                stageMutationIsPending
                || !editingStage
              }
              onClick={() =>
                setStagePendingDelete(editingStage)
              }
            >
              Eliminar etapa
            </Button>

            <Button
              onClick={handleCloseStageConfiguration}
              disabled={stageMutationIsPending}
            >
              Cerrar
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(stagePendingDelete)}
        onClose={() => {
          if (!deleteStageMutation.isPending) {
            setStagePendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Eliminar etapa
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography>
              Vas a eliminar la etapa{' '}
              <strong>
                {stagePendingDelete?.name}
              </strong>
              .
            </Typography>

            <Alert severity="warning" variant="outlined">
              Esta acción solo se permitirá si el flujo todavía
              no contiene expedientes. La configuración dependiente
              de esta etapa también dejará de estar disponible.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={deleteStageMutation.isPending}
            onClick={() => setStagePendingDelete(null)}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteRounded />}
            disabled={deleteStageMutation.isPending}
            onClick={handleConfirmDeleteStage}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={processDialogOpen}
        onClose={() => setProcessDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: `1px solid ${alpha(
              '#38bdf8',
              0.22,
            )}`,
            bgcolor: 'rgba(15,23,42,0.96)',
            backgroundImage:
              'linear-gradient(180deg, rgba(56,189,248,0.08), rgba(15,23,42,0) 38%)',
            boxShadow:
              '0 24px 80px rgba(2,6,23,0.62)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: {
              xs: 2,
              md: 2.75,
            },
            pt: 2.35,
            pb: 1.4,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1.4}
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#38bdf8', 0.14),
                  border: `1px solid ${alpha(
                    '#38bdf8',
                    0.28,
                  )}`,
                }}
              >
                <AccountTreeRounded
                  sx={{
                    color: '#67e8f9',
                  }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                    fontSize: '1.18rem',
                    lineHeight: 1.15,
                  }}
                >
                  {editingProcess
                    ? 'Editar flujo de trabajo'
                    : 'Nuevo flujo de trabajo'}
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.84rem',
                    mt: 0.45,
                  }}
                >
                  Define el proceso que agrupará etapas, tareas, responsables y expedientes.
                </Typography>
              </Box>
            </Stack>

            <Chip
              size="small"
              label="Configuración base"
              sx={{
                color: '#bae6fd',
                bgcolor: alpha('#38bdf8', 0.12),
                border: `1px solid ${alpha(
                  '#38bdf8',
                  0.22,
                )}`,
                fontWeight: 800,
              }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            px: {
              xs: 2,
              md: 2.75,
            },
            pb: 1.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.25,
              bgcolor: 'rgba(2,6,23,0.28)',
            },
            '& .MuiInputLabel-root': {
              color: '#a8b5c7',
            },
            '& .MuiFormHelperText-root': {
              color: '#94a3b8',
              mx: 0,
              mt: 0.65,
            },
          }}
        >
          <Stack spacing={2.2} sx={{ pt: 0.5 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(
                  '#38bdf8',
                  0.16,
                )}`,
                bgcolor: 'rgba(2,6,23,0.22)',
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontSize: '0.86rem',
                  lineHeight: 1.55,
                }}
              >
                Un flujo define cómo se gestionan los expedientes: sus etapas, tareas automáticas, responsables y salidas posibles.
              </Typography>
            </Paper>

            <Stack spacing={1.15}>
              <Typography
                sx={{
                  color: '#e2e8f0',
                  fontWeight: 950,
                  fontSize: '0.82rem',
                  letterSpacing: 0.55,
                  textTransform: 'uppercase',
                }}
              >
                Datos del flujo
              </Typography>

              <TextField
              label="Nombre"
              value={processForm.name}
              onChange={(event) =>
                setProcessForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              fullWidth
              required
            />

            <TextField
              label="Descripción"
              value={processForm.description}
              onChange={(event) =>
                setProcessForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={3}
            />

            {editingProcess ? (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: `1px solid ${alpha(
                    '#94a3b8',
                    0.16,
                  )}`,
                  bgcolor: 'rgba(2,6,23,0.18)',
                  px: 1.4,
                  py: 1,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={processForm.is_active}
                      onChange={(event) =>
                        setProcessForm((current) => ({
                          ...current,
                          is_active: event.target.checked,
                        }))
                      }
                    />
                  }
                  label="Disponible para crear expedientes"
                />
              </Paper>
            ) : null}
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: {
              xs: 2,
              md: 2.75,
            },
            py: 1.8,
            borderTop: `1px solid ${alpha(
              '#94a3b8',
              0.14,
            )}`,
            bgcolor: 'rgba(2,6,23,0.22)',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: 1 }}
          >
            {editingProcess ? (
              <Button
                color="error"
                variant="outlined"
                startIcon={<DeleteRounded />}
                disabled={processMutationIsPending}
                onClick={() =>
                  setProcessPendingDelete(editingProcess)
                }
              >
                Eliminar flujo
              </Button>
            ) : (
              <Box />
            )}

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setProcessDialogOpen(false)}
                disabled={processMutationIsPending}
              >
                Cancelar
              </Button>

              <Button
                variant="contained"
                disabled={processMutationIsPending}
                onClick={handleSaveProcess}
              >
                Guardar
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(processPendingDelete)}
        onClose={() => {
          if (!deleteProcessMutation.isPending) {
            setProcessPendingDelete(null);
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Eliminar flujo
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography>
              Vas a eliminar el flujo{' '}
              <strong>
                {processPendingDelete?.name}
              </strong>
              .
            </Typography>

            <Alert severity="warning" variant="outlined">
              Esta acción eliminará también su configuración
              dependiente. Solo se permitirá si el flujo todavía
              no contiene expedientes.
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            disabled={deleteProcessMutation.isPending}
            onClick={() => setProcessPendingDelete(null)}
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            startIcon={<DeleteRounded />}
            disabled={deleteProcessMutation.isPending}
            onClick={handleConfirmDeleteProcess}
          >
            Confirmar eliminación
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={stageDialogOpen}
        onClose={() => setStageDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: `1px solid ${alpha(
              '#38bdf8',
              0.22,
            )}`,
            bgcolor: 'rgba(15,23,42,0.96)',
            backgroundImage:
              'linear-gradient(180deg, rgba(56,189,248,0.08), rgba(15,23,42,0) 38%)',
            boxShadow:
              '0 24px 80px rgba(2,6,23,0.62)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: {
              xs: 2,
              md: 2.75,
            },
            pt: 2.35,
            pb: 1.4,
          }}
        >
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={1.4}
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1.2}
              alignItems="center"
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#38bdf8', 0.14),
                  border: `1px solid ${alpha(
                    '#38bdf8',
                    0.28,
                  )}`,
                }}
              >
                <FlagRounded
                  sx={{
                    color: '#67e8f9',
                  }}
                />
              </Box>

              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontWeight: 950,
                    fontSize: '1.18rem',
                    lineHeight: 1.15,
                  }}
                >
                  {editingStage
                    ? 'Editar etapa del flujo'
                    : 'Nueva etapa del flujo'}
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.84rem',
                    mt: 0.45,
                  }}
                >
                  Define cómo se comporta esta fase, quién la gestiona y cuándo debe avanzar.
                </Typography>
              </Box>
            </Stack>

            <Chip
              size="small"
              label="Diseño del flujo"
              sx={{
                color: '#bae6fd',
                bgcolor: alpha('#38bdf8', 0.12),
                border: `1px solid ${alpha(
                  '#38bdf8',
                  0.22,
                )}`,
                fontWeight: 800,
              }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            px: {
              xs: 2,
              md: 2.75,
            },
            pb: 1.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.25,
              bgcolor: 'rgba(2,6,23,0.28)',
            },
            '& .MuiInputLabel-root': {
              color: '#a8b5c7',
            },
            '& .MuiFormHelperText-root': {
              color: '#94a3b8',
              mx: 0,
              mt: 0.65,
            },
          }}
        >
          <Stack spacing={2.2} sx={{ pt: 0.5 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${alpha(
                  '#38bdf8',
                  0.16,
                )}`,
                bgcolor: 'rgba(2,6,23,0.22)',
                px: 1.5,
                py: 1.25,
              }}
            >
              <Typography
                sx={{
                  color: '#cbd5e1',
                  fontSize: '0.86rem',
                  lineHeight: 1.55,
                }}
              >
                Una etapa representa una fase operativa del flujo. Después de crearla podrás añadir sus tareas automáticas y las salidas posibles.
              </Typography>
            </Paper>

            <Stack spacing={1.15}>
              <Typography
                sx={{
                  color: '#e2e8f0',
                  fontWeight: 950,
                  fontSize: '0.82rem',
                  letterSpacing: 0.55,
                  textTransform: 'uppercase',
                }}
              >
                Datos básicos
              </Typography>

              <TextField
              label="Nombre"
              value={stageForm.name}
              onChange={(event) =>
                setStageForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              fullWidth
              required
            />

            <TextField
              label="Descripción"
              value={stageForm.description}
              onChange={(event) =>
                setStageForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={3}
            />
            </Stack>

            <Stack spacing={1.15}>
              <Typography
                sx={{
                  color: '#e2e8f0',
                  fontWeight: 950,
                  fontSize: '0.82rem',
                  letterSpacing: 0.55,
                  textTransform: 'uppercase',
                }}
              >
                Comportamiento y responsables
              </Typography>

              <TextField
              select
              label="Tipo de etapa"
              value={stageForm.stage_type}
              onChange={(event) => {
                const nextStageType =
                  event.target
                    .value as IndustrialWorkflowStageType;

                setStageForm((current) => ({
                  ...current,

                  stage_type:
                    nextStageType,

                  is_initial:
                    nextStageType === 'closure'
                      ? false
                      : current.is_initial,

                  is_final:
                    nextStageType === 'closure',

                  execution_work_area_id:
                    nextStageType === 'outside_system_wait'
                    || nextStageType === 'closure'
                      ? ''
                      : current.execution_work_area_id,

                  tracking_work_area_id:
                    nextStageType === 'closure'
                      ? ''
                      : current.tracking_work_area_id,

                  outside_system_actor:
                    nextStageType === 'outside_system_wait'
                      ? current.outside_system_actor
                      : '',

                  outside_system_action:
                    nextStageType === 'outside_system_wait'
                      ? current.outside_system_action
                      : '',
                }));
              }}
              helperText="Elige cómo se comporta esta fase dentro del flujo."
              fullWidth
            >
              <MenuItem value="entry">
                Primera etapa del flujo
              </MenuItem>

              <MenuItem value="app_work">
                Trabajo interno
              </MenuItem>

              <MenuItem value="outside_system_wait">
                Espera de proveedor, cliente o tercero
              </MenuItem>

              <MenuItem value="closure">
                Cierre del expediente
              </MenuItem>
            </TextField>

            {stageForm.stage_type === 'entry'
            || stageForm.stage_type === 'app_work' ? (
              <TextField
                select
                label="Área ejecutora"
                value={
                  stageForm.execution_work_area_id
                }
                onChange={(event) =>
                  setStageForm((current) => ({
                    ...current,
                    execution_work_area_id:
                      event.target.value,
                  }))
                }
                helperText="Área que realiza el trabajo operativo dentro de la app."
                fullWidth
              >
                <MenuItem value="">
                  Sin área ejecutora
                </MenuItem>

                {(workAreasQuery.data ?? [])
                  .filter(
                    (area) =>
                      area.is_active,
                  )
                  .map(
                    (area) => (
                      <MenuItem
                        key={area.id}
                        value={area.id}
                      >
                        {area.name}
                      </MenuItem>
                    ),
                  )}
              </TextField>
            ) : null}

            {stageForm.stage_type !== 'closure' ? (
              <TextField
                select
                label="Área de seguimiento"
                value={
                  stageForm.tracking_work_area_id
                }
                onChange={(event) =>
                  setStageForm((current) => ({
                    ...current,
                    tracking_work_area_id:
                      event.target.value,
                  }))
                }
                helperText="Área responsable de supervisar que el expediente avance."
                fullWidth
              >
                <MenuItem value="">
                  Sin área de seguimiento
                </MenuItem>

                {(workAreasQuery.data ?? [])
                  .filter(
                    (area) =>
                      area.is_active,
                  )
                  .map(
                    (area) => (
                      <MenuItem
                        key={area.id}
                        value={area.id}
                      >
                        {area.name}
                      </MenuItem>
                    ),
                  )}
              </TextField>
            ) : null}

            {stageForm.stage_type
            === 'outside_system_wait' ? (
              <>
                <TextField
                  label="Actor fuera del sistema"
                  value={
                    stageForm.outside_system_actor
                  }
                  onChange={(event) =>
                    setStageForm((current) => ({
                      ...current,
                      outside_system_actor:
                        event.target.value,
                    }))
                  }
                  helperText="Ejemplo: Cliente, fábrica, proveedor o laboratorio."
                  fullWidth
                />

                <TextField
                  label="Acción o respuesta esperada"
                  value={
                    stageForm.outside_system_action
                  }
                  onChange={(event) =>
                    setStageForm((current) => ({
                      ...current,
                      outside_system_action:
                        event.target.value,
                    }))
                  }
                  helperText="Describe qué debe suceder antes de continuar."
                  multiline
                  minRows={2}
                  fullWidth
                />
              </>
            ) : null}
            </Stack>

            <Stack spacing={1.15}>
              <Typography
                sx={{
                  color: '#e2e8f0',
                  fontWeight: 950,
                  fontSize: '0.82rem',
                  letterSpacing: 0.55,
                  textTransform: 'uppercase',
                }}
              >
                Planificación y reglas
              </Typography>

              <TextField
              label="Plazo general de la etapa en días"
              value={stageForm.expected_duration_days}
              onChange={(event) =>
                setStageForm((current) => ({
                  ...current,
                  expected_duration_days: event.target.value,
                }))
              }
              type="number"
              fullWidth
            />

            <Stack spacing={0.25}>
              <FormControlLabel
                control={
                  <Switch
                    checked={stageForm.is_initial}
                    onChange={(event) =>
                      setStageForm((current) => ({
                        ...current,

                        is_initial:
                          event.target.checked,

                        is_final:
                          event.target.checked
                            ? false
                            : current.is_final,

                        stage_type:
                          event.target.checked
                            && current.stage_type
                            === 'closure'
                              ? 'entry'
                              : current.stage_type,
                      }))
                    }
                  />
                }
                label="Etapa inicial del proceso"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={stageForm.is_final}
                    onChange={(event) =>
                      setStageForm((current) => ({
                        ...current,

                        is_final:
                          event.target.checked,

                        is_initial:
                          event.target.checked
                            ? false
                            : current.is_initial,

                        stage_type:
                          event.target.checked
                            ? 'closure'
                            : current.stage_type
                              === 'closure'
                                ? 'app_work'
                                : current.stage_type,

                        execution_work_area_id:
                          event.target.checked
                            ? ''
                            : current
                                .execution_work_area_id,

                        tracking_work_area_id:
                          event.target.checked
                            ? ''
                            : current
                                .tracking_work_area_id,

                        outside_system_actor:
                          event.target.checked
                            ? ''
                            : current
                                .outside_system_actor,

                        outside_system_action:
                          event.target.checked
                            ? ''
                            : current
                                .outside_system_action,
                      }))
                    }
                  />
                }
                label="Etapa final"
              />
            </Stack>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: {
              xs: 2,
              md: 2.75,
            },
            py: 1.8,
            borderTop: `1px solid ${alpha(
              '#94a3b8',
              0.14,
            )}`,
            bgcolor: 'rgba(2,6,23,0.22)',
          }}
        >
          <Button
            onClick={() => setStageDialogOpen(false)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={stageMutationIsPending}
            onClick={handleSaveStage}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}